import type { ChildrenArray } from 'react'
import React, { Component } from 'react'
import ReactDOM from 'react-dom'
import { Link } from 'app/common/link'
import Responsive from 'react-responsive'
import styled, { css } from 'styled-components'
import classNames from 'classnames'
import { NavigationMenu } from 'app/common/navigation-menu'
import { NavigationItemIcon } from 'app/common/navigation-item-icon'
import {
    breakpoints,
    LogoContainer,
    MainNavigation,
    media,
    NavContainer,
    ResponsiveContainer,
    SiteHeader
} from 'app/assets/styles'
import { colors } from 'app-v2/design-systems/theme/colors'
import { BagOnClick, Close, Hamburger, HandIcon } from 'app/assets/icons'
import ExecutionEnvironment from 'exenv'
import { throttle } from 'throttle-debounce'
import Modal from 'react-modal'
import type { EventMetadata } from 'shared/store/metadata'
import { eventsDefinitions } from 'shared/store/metadata'
import { fromEvent, merge } from 'rxjs'
import { debounceTime, map } from 'rxjs/operators'
import { Transition } from 'react-transition-group'
import { StoreQuickSearch } from './store-quick-search'
import { KingdomPopin } from './kingdom-popin'
import type { MenuItemDTO } from 'shared/types'
import { Insert } from 'app/common/insert'
import {
    isAndroid,
    isMobile as rddIsMobile,
    isTablet
} from 'react-device-detect'
import type { IntlShape } from 'react-intl'
import { injectIntl } from 'react-intl'
import { FeatureToggle } from 'app/common/feature-toggle'
import { CcCartCheck } from 'app/ecommerce/click-and-collect/cart-check'
import { featureCodes } from 'shared/utils'
import { Icon } from 'app-v2/shared/features/icon'
import { tokens } from 'app-v2/shared/features/icon/utils/tokens'
import { IconButton } from 'app-v2/design-systems/components/inputs/button'
import { IconButtonMobile } from 'app-v2/design-systems/components/inputs/button/IconButtonMobile'
import {
    marketSelect,
    removeLocalitiesContainerFromDOM
} from 'app/common/utils'
import logoReunion from 'shared/assets/images/logo-reunion.png'

export type HeaderBackground = 'transparent' | 'gradient' | 'plain'

type Props = {
    location: Object,
    desktopLeftItems: Array<MenuItemDTO>,
    desktopRightItems: Array<MenuItemDTO>,
    withQuickSearch?: boolean,
    shouldStick?: boolean,
    border?: boolean,
    color?: string,
    background?: HeaderBackground,
    open: boolean,
    sendMeta: EventMetadata => void,
    showAppBanner: boolean,
    intl: IntlShape
}

type State = {
    open: boolean,
    sticky: boolean,
    hoverMainNavigation: boolean,
    hoverQuickSearch: boolean,
    shouldDisplayQuickSearch: boolean,
    hoverKingdom: boolean,
    width: number
}

class _HeaderComponent extends Component<Props, State> {
    mainNavigationRef: ?any
    storeQuickSearchRef: ?any
    kingdomPopinRef: ?any
    siteHeaderRef: ?any
    storeLocatorIconRef: ?any
    kingdomIconRef: ?any
    hoverMainNavigationSubscription: ?any
    hoverQuickSearchSubscription: ?any
    hoverKingdomPopinSubscription: ?any

    state = {
        open: false,
        sticky: false,
        hoverMainNavigation: false,
        hoverQuickSearch: false,
        shouldDisplayQuickSearch: false,
        hoverKingdom: false,
        width: window.innerWidth
    }

    componentDidMount() {
        const { shouldStick } = this.props
        if (ExecutionEnvironment.canUseEventListeners && shouldStick) {
            window.addEventListener('scroll', this.handleScroll)
        }
        this.mountHoverBehaviour()
        window.addEventListener('resize', this.updateWindowDimensions)
    }

    componentDidUpdate() {
        this.mountHoverBehaviour()
    }

    updateWindowDimensions = () => this.setState({ width: window.innerWidth })

    mountHoverBehaviour() {
        if (
            ExecutionEnvironment.canUseDOM &&
            ExecutionEnvironment.canUseEventListeners &&
            this.mainNavigationRef &&
            this.storeQuickSearchRef &&
            this.siteHeaderRef &&
            this.storeLocatorIconRef &&
            this.kingdomPopinRef &&
            this.kingdomIconRef &&
            !this.hoverMainNavigationSubscription &&
            !this.hoverQuickSearchSubscription &&
            !this.hoverKingdomPopinSubscription
        ) {
            const mainNavigationNode = ReactDOM.findDOMNode(
                this.mainNavigationRef
            )
            const storeQuickSearchNode = ReactDOM.findDOMNode(
                this.storeQuickSearchRef
            )
            const siteHeaderNode = ReactDOM.findDOMNode(this.siteHeaderRef)
            const storeLocatorIconNode = ReactDOM.findDOMNode(
                this.storeLocatorIconRef
            )
            const kingdomIconNode = ReactDOM.findDOMNode(this.kingdomIconRef)
            const kingdomPopinNode = ReactDOM.findDOMNode(this.kingdomPopinRef)

            this.hoverMainNavigationSubscription = merge(
                fromEvent(mainNavigationNode, 'mouseenter'),
                fromEvent(mainNavigationNode, 'mouseleave'),
                fromEvent(storeQuickSearchNode, 'mouseenter'),
                fromEvent(storeQuickSearchNode, 'mouseleave'),
                fromEvent(kingdomPopinNode, 'mouseenter'),
                fromEvent(kingdomPopinNode, 'mouseleave')
            )
                .pipe(debounceTime(200))
                .pipe(map(e => e.type === 'mouseenter'))
                .subscribe(hover =>
                    this.setState({ hoverMainNavigation: hover })
                )

            this.hoverQuickSearchSubscription = merge(
                fromEvent(storeQuickSearchNode, 'mouseenter'),
                fromEvent(storeLocatorIconNode, 'mouseenter'),
                fromEvent(siteHeaderNode, 'mouseleave'),
                fromEvent(mainNavigationNode, 'click')
            )
                .pipe(debounceTime(200))
                .pipe(map(e => e.type === 'mouseenter'))
                .subscribe(hover => {
                    this.setState({ hoverQuickSearch: hover })
                    this.setState({ hoverKingdom: false })
                    if (!hover) {
                        !this.state.shouldDisplayQuickSearch &&
                            removeLocalitiesContainerFromDOM()
                    }
                })

            this.hoverKingdomPopinSubscription = merge(
                fromEvent(kingdomPopinNode, 'mouseenter'),
                fromEvent(kingdomIconNode, 'mouseenter'),
                fromEvent(siteHeaderNode, 'mouseleave'),
                fromEvent(mainNavigationNode, 'click')
            )
                .pipe(debounceTime(200))
                .pipe(map(e => e.type === 'mouseenter'))
                .subscribe(hover => {
                    this.setState({ hoverKingdom: hover })
                    this.setState({ hoverQuickSearch: false })

                    if (hover) {
                        removeLocalitiesContainerFromDOM()
                        this.setState({
                            shouldDisplayQuickSearch: false
                        })
                    }
                })
        }
    }

    componentWillUnmount() {
        if (
            ExecutionEnvironment.canUseEventListeners &&
            this.props.shouldStick
        ) {
            window.removeEventListener('scroll', this.handleScroll)
            if (this.hoverMainNavigationSubscription) {
                this.hoverMainNavigationSubscription.unsubscribe()
            }
            if (this.hoverQuickSearchSubscription) {
                this.hoverQuickSearchSubscription.unsubscribe()
            }
            if (this.hoverKingdomPopinSubscription) {
                this.hoverKingdomPopinSubscription.unsubscribe()
            }
        }
        window.removeEventListener('resize', this.updateWindowDimensions)
    }

    handleScroll = (e: SyntheticEvent<any>) => {
        throttle(
            300,
            this.setState({
                sticky: window.scrollY
                    ? window.scrollY > 0
                    : window.pageYOffset > 0
            })
        )
    }

    handleClose = () =>
        this.setState({
            hoverQuickSearch: false
        })

    handleOnQuickSearchChange = (searchValue?: string) => {
        this.setState({
            shouldDisplayQuickSearch: !!searchValue
        })
    }

    closeNavMenu = () => {
        this.setState({ open: false })
    }

    toggleNavigation = () => {
        const open = !this.state.open
        const metaDisplay = open
            ? eventsDefinitions['BURGER_MENU_OPEN']
            : eventsDefinitions['BURGER_MENU_CLOSE']
        this.props.sendMeta(metaDisplay)
        this.setState({ open })
    }

    renderNavItems = (items: Array<MenuItemDTO>): ChildrenArray<any> => {
        const { width, open, hoverKingdom } = this.state
        const { sendMeta } = this.props
        const isMobile = width < breakpoints.medium
        return (
            items &&
            items.map((item, index) => {
                const { actionUrl, label, external, itemType } = item
                if (!itemType) {
                    return null
                }
                const metaData = {
                    ...eventsDefinitions[itemType],
                    context: {
                        type: 'header',
                        path: this.props.location.pathname,
                        label
                    }
                }
                const ga4Meta = {
                    ...eventsDefinitions.GA4_USER_NAVIGATION,
                    access_type: 'header',
                    cta_name: label
                }

                const iconLink = (
                    <StyledIconLink
                        external={external}
                        url={actionUrl}
                        meta={[metaData, ga4Meta]}>
                        <HoverableLabel>
                            <NavigationItemIcon {...item} />
                        </HoverableLabel>
                    </StyledIconLink>
                )

                switch (itemType.toUpperCase()) {
                    case 'STORES':
                        return (
                            <HoverableNavItem
                                key={index}
                                ref={node => (this.storeLocatorIconRef = node)}>
                                {iconLink}
                            </HoverableNavItem>
                        )
                    case 'DELIVERY':
                        return (
                            <HoverableNavItem key={index}>
                                {iconLink}
                            </HoverableNavItem>
                        )
                    case 'ACCOUNT':
                        return (
                            <HoverableNavItem
                                key={index}
                                ref={node => (this.kingdomIconRef = node)}>
                                {iconLink}
                                <Transition in timeout={0}>
                                    <RefContainer
                                        ref={node =>
                                            (this.kingdomPopinRef = node)
                                        }>
                                        {hoverKingdom && (
                                            <Responsive
                                                minWidth={
                                                    breakpoints.medium - 1
                                                }>
                                                <KingdomPopin />
                                            </Responsive>
                                        )}
                                    </RefContainer>
                                </Transition>
                            </HoverableNavItem>
                        )
                    case 'ORDER':
                        return rddIsMobile || isTablet ? (
                            <FeatureToggle
                                features={[
                                    featureCodes.CLICK_AND_COLLECT,
                                    featureCodes.TABLE_ORDER
                                ]}
                                key={index}>
                                <CcCartCheck
                                    onClick={() => sendMeta(metaData)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}>
                                    <CcButtonContainer>
                                        <IconButtonMobile
                                            icon={
                                                <HandIcon
                                                    color={colors.mayoEggWhite}
                                                />
                                            }
                                        />
                                    </CcButtonContainer>
                                </CcCartCheck>
                            </FeatureToggle>
                        ) : (
                            <FeatureToggle
                                feature={featureCodes.CLICK_AND_COLLECT}
                                key={index}>
                                <CcCartCheck
                                    onClick={() => sendMeta(metaData)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}>
                                    <CcButtonContainer>
                                        {isMobile ? (
                                            <IconButtonMobile
                                                icon={
                                                    <BagOnClick
                                                        size={25}
                                                        color={
                                                            colors.mayoEggWhite
                                                        }
                                                    />
                                                }
                                            />
                                        ) : (
                                            <IconButton
                                                icon={
                                                    <Icon
                                                        code={tokens.ccHand}
                                                        size={25}
                                                        marginRight={5}
                                                        color={
                                                            colors.mayoEggWhite
                                                        }
                                                    />
                                                }>
                                                {label}
                                            </IconButton>
                                        )}
                                    </CcButtonContainer>
                                </CcCartCheck>
                            </FeatureToggle>
                        )
                    default:
                        return (
                            <HoverableNavItem key={index}>
                                <StyledIconLink
                                    external={external}
                                    url={actionUrl ? actionUrl : ''}
                                    meta={[metaData, ga4Meta]}>
                                    <HoverableLabel open={open}>
                                        {label}
                                    </HoverableLabel>
                                </StyledIconLink>
                            </HoverableNavItem>
                        )
                }
            })
        )
    }

    render() {
        const {
            sticky,
            open,
            hoverMainNavigation,
            hoverQuickSearch,
            shouldDisplayQuickSearch
        } = this.state
        const {
            desktopLeftItems,
            desktopRightItems,
            withQuickSearch,
            shouldStick = true,
            border = false,
            background = '',
            showAppBanner
        } = this.props

        const classes = classNames({
            open: open,
            sticky: sticky,
            hover: hoverMainNavigation
        })

        //$FlowFixMe
        const isTPKRoute = window.location.pathname?.includes('tupreferesking')

        const burgerMenu = (
            <Responsive maxWidth={1200}>
                {smallScreens => {
                    return (
                        <NavItems>
                            <NavItem>
                                <BurgerButtonContainer
                                    onClick={this.toggleNavigation}>
                                    {open ? (
                                        <Close color={colors.bbqBrown} />
                                    ) : (
                                        <Hamburger color={colors.bbqBrown} />
                                    )}
                                </BurgerButtonContainer>
                            </NavItem>
                            {!smallScreens &&
                                this.renderNavItems(desktopLeftItems)}
                        </NavItems>
                    )
                }}
            </Responsive>
        )

        const mainNavigation = (
            <MainNavigation
                ref={node => (this.mainNavigationRef = node)}
                showAppBanner={showAppBanner && !open}>
                <ResponsiveContainer>
                    <NavContainer>
                        {burgerMenu}
                        <LogoContainer>
                            <Link
                                onClick={this.closeNavMenu}
                                url="/"
                                meta={eventsDefinitions.LOGO}>
                                {marketSelect({
                                    france: (
                                        <LogoBK
                                            code={tokens.logo}
                                            background={colors.mayoEggWhite}
                                        />
                                    ),
                                    reunion: <LogoBKImg src={logoReunion} />
                                })}
                            </Link>
                        </LogoContainer>
                        <NavItems>
                            {this.renderNavItems(desktopRightItems)}
                        </NavItems>
                    </NavContainer>
                </ResponsiveContainer>
            </MainNavigation>
        )

        const storeQuickSearch = withQuickSearch && (
            <Transition in timeout={0}>
                {state => (
                    <RefContainer
                        className={`quick-search quick-search-${state}`}
                        ref={node => (this.storeQuickSearchRef = node)}>
                        {(hoverQuickSearch || shouldDisplayQuickSearch) && (
                            <Responsive minWidth={breakpoints.medium}>
                                <StoreQuickSearch
                                    handleClose={this.handleClose}
                                    handleOnQuickSearchChange={
                                        this.handleOnQuickSearchChange
                                    }
                                />
                            </Responsive>
                        )}
                    </RefContainer>
                )}
            </Transition>
        )

        const navigationMenuModal = (
            <Modal
                isOpen={open}
                ariaHideApp={false}
                style={navigationMenuModalStyle}>
                {mainNavigation}
                <NavigationMenu close={this.closeNavMenu} />
            </Modal>
        )

        return (
            <SiteHeader
                ref={node => (this.siteHeaderRef = node)}
                className={classes}
                shouldStick={shouldStick}
                border={border}
                color={colors.bbqBrown}
                background={shouldDisplayQuickSearch ? 'plain' : background}>
                {isAndroid && !isTPKRoute && <Insert />}
                <div className="site-header">
                    {mainNavigation}
                    {storeQuickSearch}
                    {navigationMenuModal}
                </div>
            </SiteHeader>
        )
    }
}

const navigationMenuModalStyle = {
    overlay: {
        zIndex: 10000
    },
    content: {
        padding: 0,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        border: 'none',
        borderRadius: 0,
        outline: 'none',
        height: '100%',
        backgroundColor: colors.mayoEggWhite
    }
}

const LogoBK = styled(Icon)`
    width: 50px;
    height: 50px;

    ${media.medium`
        width: 80px;
        height: 80px;
    `};
`

const LogoBKImg = styled.img`
    width: 50px;
    height: 50px;

    ${media.medium`
        width: 80px;
        height: 80px;
    `};
`

const NavItems = styled.ul`
    margin: 0;
    display: flex;
`
const NavItem = styled.li`
    display: flex;
    align-items: center;
    justify-content: center;
`

const StyledIconLink = styled(Link)`
    padding: 7px 15px;
    &:last-of-type {
        padding-right: 0;
    }
`

const buttonNoOutline = css`
    border: none;
    outline: 0;
    cursor: pointer;
    &:focus {
        outline: 0;
    }
`
const BurgerButtonContainer = styled.button`
    padding: 5px 15px 5px 5px;
    font-size: 0;
    ${buttonNoOutline};
    transform: translateX(-10px);
    background: none;
`

const HoverableNavItem = styled(NavItem)`
    position: relative;
`

const Label = styled.div`
    font-family: 'Roboto Condensed';
    font-weight: normal;
`

const HoverableLabel = styled(Label)`
    padding: 8px 0;
    display: flex;
    position: relative;
    overflow: hidden;
    &:after {
        content: '';
        position: absolute;
        left: 0;
        top: calc(100% - 2px);
        transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
        display: inline-block;
        height: 2px;
        width: 100%;
        transform: translateX(-101%);
        background: #f5a528;
    }
    ${HoverableNavItem}:hover &,
    .active & {
        &:after {
            transform: translateX(0);
        }
    }
    ${props => props.open && `color: ${colors.bbqBrown}`};
`

const CcButtonContainer = styled.div`
    display: flex;
    align-content: center;
    margin-left: 15px;
`

const RefContainer = styled.div``

export const HeaderComponent = injectIntl(_HeaderComponent)
